import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Bar, BarChart
} from 'recharts';
import { 
  Grid,
  PagingPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
  ExportPanel,
} from '@devexpress/dx-react-grid-material-ui';
import {
  IntegratedPaging,
  PagingState,
  DataTypeProvider,
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import saveAs from 'file-saver';
import moment from 'moment';
import 'moment/locale/de';
import { Close as CloseIcon } from '@mui/icons-material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import EuroOutlinedIcon from '@mui/icons-material/EuroOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ContentHeader from '../../components/UI/ContentHeader';
import CommonModal from '../../components/UI/CommonModal';
import CommonButton from '../../components/UI/button';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download.svg';
import { api } from '../../services';
import * as S from './styled';
import { useContextInfo } from '../../context';
import { getDirectusToken } from '../../utils/localStorage';
import { loginDirectus } from '../../services/api/directus';

const BillingPage = () => {
  const { i18n } = useTranslation();
  const {
    userInfo: userInfoData,
  } = useContextInfo();
  const { info: userInfo } = userInfoData;

  const [reimbursementRows, setReimbursementRows] = useState([]);
  const [reimbursementLoading, setReimbursementLoading] = useState(false);
  const [reimbursementError, setReimbursementError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const exporterRef = React.useRef(null);
  const { t } = useTranslation();
  // States für smarte Features
  const [filters, setFilters] = useState({
    status: 'all',
    year: new Date().getFullYear(),
    searchTerm: ''
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [sorting, setSorting] = useState([{ columnName: 'submissionDate', direction: 'desc' }]);
  const [searchValue, setSearchValue] = useState('');

  moment.locale(i18n.language);

  // Helper Funktionen für Zahlenformatierung
  const formatNumber = (number, decimals = 0) => {
    return new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(number);
  };

  const formatCurrency = (amount, showSymbol = true) => {
    return new Intl.NumberFormat(i18n.language, {
      style: showSymbol ? 'currency' : 'decimal',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatKWh = (amount, decimals = 1) => {
    return `${formatNumber(amount, decimals)} kWh`;
  };

  // DUMMY Response für Billing Data
  const billingDataDummy = {
    currentYearCharge: 1122, // kWh
    currentElectricityPrice: 0.31, // EUR
    totalReimbursed: 861*0.31 // EUR
  };

  const normalizeStatus = (status) => {
    if (!status) return 'new';
    const normalized = String(status).trim().toLowerCase();
    switch (normalized) {
      case 'clarification':
        return 'clarification';
      case 'approved':
        return 'approved';
      case 'credited':
        return 'credited';
      case 'new':
        return 'new';
      case 'error':
        return 'error';
      default:
        return normalized;
    }
  };

  const statusPriority = {
    error: 5,
    clarification: 4,
    new: 3,
    approved: 2,
    credited: 1
  };

  const monthlyChartData = useMemo(() => {
    const grouped = new Map();

    reimbursementRows.forEach((row) => {
      const date = row.billingPeriod || row.submissionDate || row.reimbursementDate;
      if (!date) return;

      const monthKey = moment(date).format('YYYY-MM');
      const label = moment(date).format('MMM');

      if (!grouped.has(monthKey)) {
        grouped.set(monthKey, {
          month: label,
          amount: 0,
          status: normalizeStatus(row.status),
          latestDate: date
        });
      }

      const bucket = grouped.get(monthKey);
      bucket.amount += Number(row.chargeAmount || 0);

      const currentPriority = statusPriority[bucket.status] || 0;
      const incomingPriority = statusPriority[normalizeStatus(row.status)] || 0;
      if (incomingPriority > currentPriority) {
        bucket.status = normalizeStatus(row.status);
      }
      if (moment(date).isAfter(bucket.latestDate)) {
        bucket.latestDate = date;
      }
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .map(([, value]) => ({
        month: value.month,
        amount: value.amount,
        status: value.status
      }));
  }, [reimbursementRows]);

  const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL;

  const mapReimbursementItem = (item) => {
    const billingPeriod = item.billing_period || null;
    const submissionDate = item.date_created || null;
    const reimbursementDate = item.date_credited || item.date_approved || null;
    const chargingMonth = billingPeriod
      ? moment(billingPeriod).format('MMMM YYYY')
      : (submissionDate ? moment(submissionDate).format('MMMM YYYY') : '-');

    const chargeAmount = Number(item.amount_kwh || 0);
    const reimbursementAmount = Number(item.amount_currency || 0);
    const electricityPrice = chargeAmount > 0
      ? reimbursementAmount / chargeAmount
      : 0;

    const documentId = item.document && (typeof item.document === 'string' ? item.document : item.document?.id);
    return {
      id: item.id ?? `${chargingMonth}-${submissionDate || reimbursementDate || 'unknown'}`,
      chargingMonth,
      status: normalizeStatus(item.status),
      submissionDate,
      billingPeriod,
      reimbursementDate,
      additionalInfo: item.notes || item.credited_notes || '',
      chargeAmount,
      electricityPrice,
      reimbursementAmount,
      receiptUrl: item.receipt_url || null,
      receiptAssetId: documentId || null,
      currency: item.currency || null,
      driver: item.driver || null,
      vehicle: item.vehicle || null,
      orgUnit: item.org_unit || null,
      userApproved: item.user_approved || null
    };
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadReimbursements = async () => {
      setReimbursementLoading(true);
      setReimbursementError(null);

      let token = getDirectusToken();
      if (!token) {
        console.warn('[Directus] Kein Token gefunden. Login wird ausgeführt.');
        await loginDirectus(userInfo?.email, userInfo?.passwordHash);
      }

      try {
        const items = await api.directus.getReimbursements({
          signal: controller.signal,
          user: userInfo?.email,
          password: userInfo?.passwordHash
        });
        const mapped = items.map(mapReimbursementItem);

        if (isMounted) {
          setReimbursementRows(mapped);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          console.error('[Billing] Fehler beim Laden der Abrechnungen:', error);
          setReimbursementError('Abrechnungen konnten nicht geladen werden.');
          setReimbursementRows([]);
        }
      } finally {
        if (isMounted) {
          setReimbursementLoading(false);
        }
      }
    };

    loadReimbursements();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // Gefilterte Daten
  const filteredData = useMemo(() => {
    let data = [...reimbursementRows];

    // Status Filter
    if (filters.status !== 'all') {
      data = data.filter(item => item.status === filters.status);
    }

    // Jahr Filter
    /*
    const year = filters.year.toString();
    data = data.filter(item => 
      item.submissionDate.includes(year) || 
      (item.reimbursementDate && item.reimbursementDate.includes(year))
    );*/

    return data;
  }, [filters, reimbursementRows]);

  // Summary Berechnungen
  const summary = useMemo(() => {
    return {
      totalOpen: filteredData
        .filter(d => d.status === 'new')
        .reduce((sum, d) => sum + Number(d.reimbursementAmount || 0), 0),
      totalProcessing: filteredData
        .filter(d => d.status === 'clarification' || d.status === 'approved')
        .reduce((sum, d) => sum + Number(d.reimbursementAmount || 0), 0),
      totalReimbursed: filteredData
        .filter(d => d.status === 'credited')
        .reduce((sum, d) => sum + Number(d.reimbursementAmount || 0), 0),
      totalAmount: filteredData.reduce((sum, d) => sum + d.reimbursementAmount, 0)
    };
  }, [filteredData]);

  const last12MonthsCharge = useMemo(() => {
    const now = moment();
    const cutoff = moment().subtract(12, 'months').startOf('day');

    return reimbursementRows.reduce((sum, row) => {
      const dateValue = row.billingPeriod || row.submissionDate || row.reimbursementDate;
      if (!dateValue) return sum;
      const date = moment(dateValue);
      if (!date.isValid()) return sum;
      if (date.isAfter(cutoff) && date.isSameOrBefore(now)) {
        return sum + Number(row.chargeAmount || 0);
      }
      return sum;
    }, 0);
  }, [reimbursementRows]);

  const totalReimbursedAmount = useMemo(() => {
    return reimbursementRows.reduce((sum, row) => {
      if (row.status === 'credited') {
        return sum + Number(row.reimbursementAmount || 0);
      }
      return sum;
    }, 0);
  }, [reimbursementRows]);

  // API Calls (auskommentiert)
  /*
  const { data: billingData, loading: billingLoading } = useQuery(GET_BILLING_DATA, {
    variables: {
      userId: user.id,
      year: new Date().getFullYear()
    }
  });

  const { data: chartData, loading: chartLoading } = useQuery(GET_BILLING_CHART_DATA, {
    variables: {
      userId: user.id,
      months: 12
    }
  });
  */

  const getBarColor = (status) => {
    switch (status) {
      case 'new':
        return '#FF8E4F';
      case 'clarification':
        return '#FFD700';
      case 'approved':
        return '#9BE15D';
      case 'credited':
        return '#53AF31';
      case 'error':
        return '#E53935';
      default:
        return '#999999';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return '#FF8E4F';
      case 'clarification':
        return '#FFD700';
      case 'approved':
        return '#9BE15D';
      case 'credited':
        return '#53AF31';
      case 'error':
        return '#E53935';
      default:
        return '#999999';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new':
        return 'Neu';
      case 'clarification':
        return 'Klärung';
      case 'approved':
        return 'Freigegeben';
      case 'credited':
        return 'Erstattet';
      case 'error':
        return 'Fehler';
      default:
        return status;
    }
  };

  // Table Columns Configuration
  const columns = [
    { name: 'chargingMonth', title: t('billing.table.chargingMonth') },
    { name: 'status', title: t('billing.table.status') },
    { name: 'submissionDate', title: t('billing.table.submissionDate') },
    { name: 'reimbursementDate', title: t('billing.table.reimbursementDate') },
    { name: 'additionalInfo', title: t('billing.table.additionalInfo') },
    { name: 'chargeAmount', title: t('billing.table.chargeAmount') },
    { name: 'electricityPrice', title: t('billing.table.electricityPrice') },
    { name: 'reimbursementAmount', title: t('billing.table.reimbursementAmount') },
    { name: 'receipt', title: t('billing.table.receipt') }
  ];

  const defaultColumnWidths = [
    { columnName: 'chargingMonth', width: 150 },
    { columnName: 'status', width: 120 },
    { columnName: 'submissionDate', width: 130 },
    { columnName: 'reimbursementDate', width: 130 },
    { columnName: 'additionalInfo', width: 180 },
    { columnName: 'chargeAmount', width: 120 },
    { columnName: 'electricityPrice', width: 120 },
    { columnName: 'reimbursementAmount', width: 140 },
    { columnName: 'receipt', width: 100 }
  ];

  // Custom Cell Components
  const StatusCell = ({ value }) => {
    return (
      <S.StatusBadge color={getStatusColor(value)}>
        {getStatusText(value)}
      </S.StatusBadge>
    );
  };

  const DateCell = ({ value }) => {
    if (!value) return '-';
    return moment(value).format('DD.MM.YYYY');
  };

  const AmountCell = ({ value }) => {
    return formatKWh(value);
  };

  const PriceCell = ({ value }) => {
    return formatCurrency(value);
  };

  const ReceiptCell = ({ row }) => {
    if (!row.receiptUrl && !row.receiptAssetId) {
      return <span style={{ color: '#999' }}>-</span>;
    }

    const handleDownloadClick = (e) => {
      e.stopPropagation();
      row.receiptAssetId
        ? handleDownloadReceiptAdvanced(row.receiptAssetId, row.chargingMonth)
        : handleDownloadReceipt(row.receiptUrl, row.chargingMonth);
    };

    return (
      <S.DownloadButton onClick={handleDownloadClick}>
        <DownloadIcon />
      </S.DownloadButton>
    );
  };

  const StatusTypeProvider = props => (
    <DataTypeProvider
      formatterComponent={StatusCell}
      {...props}
    />
  );

  const DateTypeProvider = props => (
    <DataTypeProvider
      formatterComponent={DateCell}
      {...props}
    />
  );

  const AmountTypeProvider = props => (
    <DataTypeProvider
      formatterComponent={AmountCell}
      {...props}
    />
  );

  const PriceTypeProvider = props => (
    <DataTypeProvider
      formatterComponent={PriceCell}
      {...props}
    />
  );

  const ReceiptTypeProvider = props => (
    <DataTypeProvider
      formatterComponent={ReceiptCell}
      {...props}
    />
  );

  // Custom Tooltip für Chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload[0]) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p>{`${label}: ${formatKWh(payload[0].value, 0)}`}</p>
          <p style={{ color: getBarColor(payload[0].payload.status) }}>
            {getStatusText(payload[0].payload.status)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Handlers
  const handleDownloadReceipt = (url, chargingMonth = null) => {
    // Hier würde normalerweise der Download-Handler implementiert werden
    console.log('Download receipt:', url);
    window.open(url, '_blank');
  };

  const handleDownloadReceiptAdvanced = async (assetId, chargingMonth = null, retried = false) => {
  try {
    const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL;
    let token = getDirectusToken();

    if (!DIRECTUS_URL) {
      throw new Error('Directus-Konfiguration fehlt');
    }
    if (!token) {
      await loginDirectus(userInfo?.email, userInfo?.passwordHash);
      token = getDirectusToken();
    }
    if (!token) {
      throw new Error('Nicht authentifiziert');
    }

    const response = await fetch(`${DIRECTUS_URL}/assets/${assetId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 401 && !retried) {
      await loginDirectus(userInfo?.email, userInfo?.passwordHash);
      return handleDownloadReceiptAdvanced(assetId, chargingMonth, true);
    }
    if (response.status === 401) {
      throw new Error('Authentifizierung fehlgeschlagen');
    }

    if (response.status === 404) {
      throw new Error('Beleg nicht gefunden');
    }

    if (response.status === 403) {
      throw new Error('FORBIDDEN_DIRECTUS_FILES');
    }

    if (!response.ok) {
      throw new Error(`Server-Fehler: ${response.status}`);
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const json = await response.json();
      if (json?.errors?.length) {
        const forbidden = json.errors.some(e => e?.extensions?.code === 'FORBIDDEN' || /permission|directus_files/i.test(e?.message || ''));
        if (forbidden) throw new Error('FORBIDDEN_DIRECTUS_FILES');
        throw new Error(json.errors[0]?.message || 'Server-Fehler');
      }
      throw new Error('Unerwartete JSON-Antwort statt Datei.');
    }

    const blob = await response.blob();

    // Filename generieren - verwende chargingMonth wenn verfügbar, sonst Datum
    let filename = `Beleg_${moment().format('YYYY-MM-DD')}.pdf`;
    if (chargingMonth) {
      filename = `Beleg_${chargingMonth.replace(/\s+/g, '_')}.pdf`;
    }

    saveAs(blob, filename);

    // Erfolgs-Feedback
    console.log('Beleg erfolgreich heruntergeladen');

  } catch (error) {
    console.error('Download-Fehler:', error);

    // Spezifische Fehlermeldungen
    let errorMessage = 'Fehler beim Herunterladen des Belegs.';
    if (error.message.includes('Nicht authentifiziert')) {
      errorMessage = 'Bitte melden Sie sich erneut an.';
    } else if (error.message.includes('nicht gefunden')) {
      errorMessage = 'Der angeforderte Beleg wurde nicht gefunden.';
    } else if (error.message === 'FORBIDDEN_DIRECTUS_FILES') {
      errorMessage = 'Keine Berechtigung für den Dateizugriff.';
    } else if (error.message.includes('Authentifizierung fehlgeschlagen')) {
      errorMessage = 'Authentifizierung fehlgeschlagen. Bitte erneut anmelden.';
    }

    alert(errorMessage);
  } finally {
    // setDownloadLoading(false);
  }
};

  const onSave = (workbook) => {
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `Abrechnungen_${moment().format('YYYY-MM-DD')}.xlsx`
      );
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setDetailModalOpen(true);
  };

  const TableRow = ({ row, ...restProps }) => (
    <S.TableRowHover
      {...restProps}
      onClick={() => handleRowClick(row)}
      style={{ cursor: 'pointer' }}
    />
  );

  return (
    <S.Wrapper
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      transition={{ duration: 0.3 }}
    >
      <ContentHeader showTab={false} />

      <S.Container>
        <S.Info>{t('billing.title')}</S.Info>
        <S.InfoSubtitle>{t('billing.subtitle')}</S.InfoSubtitle>

        {/* Dashboard Widgets */}
        <S.WidgetContainer>
          <S.Widget>
            <S.WidgetTitle>{t('billing.current_year_charge')}</S.WidgetTitle>
            <S.WidgetValue>{formatKWh(last12MonthsCharge, 0)}</S.WidgetValue>
          </S.Widget>

          <S.Widget>
            <S.WidgetTitle>{t('billing.current_electricity_price')}</S.WidgetTitle>
            <S.WidgetValue>{formatCurrency(billingDataDummy.currentElectricityPrice)}</S.WidgetValue>
          </S.Widget>

          <S.Widget>
            <S.WidgetTitle>{t('billing.total_reimbursed')}</S.WidgetTitle>
            <S.WidgetValue>{formatCurrency(totalReimbursedAmount)}</S.WidgetValue>
          </S.Widget>
        </S.WidgetContainer>

        {/* Chart */}
        <S.ChartWrapper>
          <S.ChartTitle>{t('billing.monthly_overview')}</S.ChartTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis
                tickFormatter={(value) => formatNumber(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="amount"
                fill={(entry) => getBarColor(entry.status)}
                shape={(props) => {
                  const { fill, x, y, width, height, payload } = props;
                  return (
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      fill={getBarColor(payload.status)}
                    />
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </S.ChartWrapper>

        {/* Table */}
        <S.TableWrapper>
          <S.TableWrapperTitle>{t('billing.details')}</S.TableWrapperTitle>
          <S.TableSubtitle>{t('billing.detailsDescription')}</S.TableSubtitle>

        {/* Smart Summary Section */}
        <S.SummaryContainer>
          <S.SummaryItem>
            <S.SummaryLabel>Offene Beträge</S.SummaryLabel>
            <S.SummaryValue color="#FF8E4F">{formatCurrency(summary.totalOpen)}</S.SummaryValue>
          </S.SummaryItem>
          <S.SummaryItem>
            <S.SummaryLabel>In Bearbeitung</S.SummaryLabel>
            <S.SummaryValue color="#FFD700">{formatCurrency(summary.totalProcessing)}</S.SummaryValue>
          </S.SummaryItem>
          <S.SummaryItem>
            <S.SummaryLabel>Erstattete Beträge</S.SummaryLabel>
            <S.SummaryValue color="#53AF31">{formatCurrency(summary.totalReimbursed)}</S.SummaryValue>
          </S.SummaryItem>
          <S.SummaryItem>
            <S.SummaryLabel>Gesamtsumme (gefiltert)</S.SummaryLabel>
            <S.SummaryValue>{formatCurrency(summary.totalAmount)}</S.SummaryValue>
          </S.SummaryItem>
        </S.SummaryContainer>

        <Grid rows={filteredData} columns={columns}>
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
          />
          <IntegratedPaging />

          <StatusTypeProvider for={['status']} />
          <DateTypeProvider for={['submissionDate', 'reimbursementDate']} />
          <AmountTypeProvider for={['chargeAmount']} />
          <PriceTypeProvider for={['electricityPrice', 'reimbursementAmount']} />
          <ReceiptTypeProvider for={['receipt']} />

          <Table rowComponent={TableRow} />
          <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
          <TableHeaderRow />
          <PagingPanel />

          <Toolbar />
          <ExportPanel startExport={exporterRef} />
        </Grid>

        <GridExporter
          ref={exporterRef}
          rows={filteredData}
          columns={columns}
          onSave={onSave}
        />

        </S.TableWrapper>

        {/* Detail Modal */}
        <CommonModal open={detailModalOpen} onClose={() => setDetailModalOpen(false)}>
          {selectedRow && (
            <S.ModalContent>
              <S.ModalHeader>
                <div>
                  <h2>Abrechnungsdetails</h2>
                  <p>Für Lademonat {selectedRow.chargingMonth}</p>
                <S.ModalMetaRow>
                  <S.StatusChip color={getStatusColor(selectedRow.status)}>
                    {getStatusText(selectedRow.status)}
                  </S.StatusChip>
                </S.ModalMetaRow>
                </div>
              <S.CloseButton onClick={() => setDetailModalOpen(false)} aria-label="Schließen">
                  <CloseIcon />
              </S.CloseButton>
              </S.ModalHeader>

            <S.SectionBlock>
              <S.SectionTitle>Allgemein</S.SectionTitle>
              <S.DetailGrid>
                <S.DetailItem>
                  <S.DetailRow>
                    <EventOutlinedIcon />
                    <S.DetailContent>
                      <S.DetailLabel>Eingereicht am</S.DetailLabel>
                      <S.DetailValue>
                        <DateCell value={selectedRow.submissionDate} />
                      </S.DetailValue>
                    </S.DetailContent>
                  </S.DetailRow>
                </S.DetailItem>

                <S.DetailItem>
                  <S.DetailRow>
                    <EventOutlinedIcon />
                    <S.DetailContent>
                      <S.DetailLabel>Erstattet am</S.DetailLabel>
                      <S.DetailValue>
                        <DateCell value={selectedRow.reimbursementDate} />
                      </S.DetailValue>
                    </S.DetailContent>
                  </S.DetailRow>
                </S.DetailItem>

                <S.DetailItem fullWidth>
                  <S.DetailRow>
                    <NotesOutlinedIcon />
                    <S.DetailContent>
                      <S.DetailLabel>Anmerkung</S.DetailLabel>
                      <S.DetailValue>{selectedRow.additionalInfo}</S.DetailValue>
                    </S.DetailContent>
                  </S.DetailRow>
                </S.DetailItem>
              </S.DetailGrid>
            </S.SectionBlock>

            <S.Divider />

            <S.SectionBlock>
              <S.SectionTitle>Finanzen</S.SectionTitle>
              <S.DetailGrid>
                <S.DetailItem>
                  <S.DetailRow>
                    <ElectricalServicesOutlinedIcon />
                    <S.DetailContent>
                      <S.DetailLabel>Lademenge</S.DetailLabel>
                      <S.DetailValue>{formatKWh(selectedRow.chargeAmount)}</S.DetailValue>
                    </S.DetailContent>
                  </S.DetailRow>
                </S.DetailItem>

                <S.DetailItem>
                  <S.DetailRow>
                    <EuroOutlinedIcon />
                    <S.DetailContent>
                      <S.DetailLabel>Angesetzter Strompreis</S.DetailLabel>
                      <S.DetailValue>{formatCurrency(selectedRow.electricityPrice)}</S.DetailValue>
                    </S.DetailContent>
                  </S.DetailRow>
                </S.DetailItem>

                <S.DetailItem fullWidth>
                  <S.DetailRow>
                    <ReceiptLongOutlinedIcon />
                    <S.DetailContent>
                      <S.DetailLabel>Erstattungsbetrag</S.DetailLabel>
                      <S.ReimbursementAmount>{formatCurrency(selectedRow.reimbursementAmount)}</S.ReimbursementAmount>
                    </S.DetailContent>
                  </S.DetailRow>
                </S.DetailItem>
              </S.DetailGrid>
            </S.SectionBlock>

            <S.ModalActions>
              {(selectedRow.receiptUrl || selectedRow.receiptAssetId) ? (
                selectedRow.receiptAssetId
                  ? (
                    <CommonButton text={t('billing.modal_download_receipt')} textColor="white" onClick={() => handleDownloadReceiptAdvanced(selectedRow.receiptAssetId, selectedRow.chargingMonth)}>
                    </CommonButton>
                  )
                  : (
                    <CommonButton text={t('billing.modal_download_receipt')} textColor="white" onClick={() => handleDownloadReceipt(selectedRow.receiptUrl, selectedRow.chargingMonth)}>
                    </CommonButton>
                  )
              ) : (
                <S.DetailLabel>Beleg noch nicht verfügbar</S.DetailLabel>
              )}
            </S.ModalActions>
            </S.ModalContent>
          )}
        </CommonModal>
      </S.Container>
    </S.Wrapper>
  );
};



export default BillingPage;